<template>
  <Teleport to="body">
    <Transition name="story-storage-limit">
      <div
        v-if="modelValue"
        class="story-storage-limit-overlay"
        role="presentation"
        @click.self="handleOverlayClick"
      >
        <section
          class="story-storage-limit-dialog"
          :class="{ 'story-storage-limit-dialog--single-plan': availablePlans.length === 1 }"
          role="dialog"
          aria-modal="true"
          :aria-labelledby="titleId"
          :aria-describedby="descriptionId"
        >
          <header class="story-storage-limit-header">
            <h2 :id="titleId" class="story-storage-limit-title">云空间不足</h2>
            <button
              class="story-storage-limit-close"
              type="button"
              aria-label="关闭云空间不足提示"
              @click="handleCancel"
            >
              <SvgIcon
                class="story-storage-limit-close-icon story-storage-limit-close-icon--desktop"
                name="modal-close"
                :size="12"
              />
              <SvgIcon
                class="story-storage-limit-close-icon story-storage-limit-close-icon--mobile"
                name="mobile-edit-collapse"
                :size="12"
              />
            </button>
          </header>

          <p :id="descriptionId" class="story-storage-limit-description">
            您的 <strong>{{ currentPlanName }}</strong> 最多可保存
            <strong>{{ storyLimit }} 本</strong> 绘本，目前已达上限。请
            <a href="/story" @click.prevent="handleManageStories">删除不需要的绘本</a>
            或将已完成的绘本导出 PDF 保存到本地，或 升级会员 获得无限存储空间。
          </p>

          <p class="story-storage-limit-tip">升级会员，保存更多精彩作品</p>

          <div class="story-storage-limit-plans">
            <article
              v-for="plan in availablePlans"
              :key="plan.level"
              class="membership-plan"
              :class="`membership-plan--${plan.variant}`"
            >
              <span class="membership-plan-icon" aria-hidden="true">
                <SvgIcon :name="plan.icon" :size="24" />
              </span>

              <div class="membership-plan-copy">
                <h3>{{ plan.name }}</h3>
                <p>
                  <span class="membership-plan-description--desktop">{{ plan.description }}</span>
                  <span class="membership-plan-description--mobile">
                    {{ plan.mobileDescription }}
                  </span>
                </p>
              </div>

              <span class="membership-plan-price">
                月均<em>¥{{ plan.monthlyPrice }}</em>
              </span>

              <button
                class="membership-plan-upgrade"
                type="button"
                @click="handleUpgrade(plan.level)"
              >
                去升级
              </button>
            </article>
          </div>
        </section>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
  import { computed, onBeforeUnmount, ref, useId, watch } from 'vue';
  import SvgIcon from '@/components/common/icon/SvgIcon.vue';
  import { fetchAipicturebookPrice } from '@/api/order';
  import {
    STORY_STORAGE_LIMITS,
    normalizeStoryMembershipLevel,
    type StoryMembershipLevel
  } from '@/utils/storyStorage';

  type UpgradeLevel = 1 | 2;

  interface MembershipPlan {
    level: UpgradeLevel;
    name: string;
    description: string;
    mobileDescription: string;
    monthlyPrice: number;
    icon: string;
    variant: 'basic' | 'premium';
  }

  interface Props {
    modelValue: boolean;
    membershipLevel: number;
    closeOnOverlay?: boolean;
  }

  const props = withDefaults(defineProps<Props>(), {
    closeOnOverlay: true
  });

  const emit = defineEmits<{
    'update:modelValue': [value: boolean];
    cancel: [];
    manage: [];
    upgrade: [level: UpgradeLevel];
  }>();

  const PLAN_NAMES: Record<StoryMembershipLevel, string> = {
    0: '免费版',
    1: '基础版',
    2: '高级版'
  };

  const MEMBERSHIP_PLANS: MembershipPlan[] = [
    {
      level: 1,
      name: '基础版会员',
      description: '保存20本，绘本生成8折，1400云币/月',
      mobileDescription: '保存20本，绘本生成8折',
      monthlyPrice: 24,
      icon: 'story-membership-basic',
      variant: 'basic'
    },
    {
      level: 2,
      name: '高级版会员',
      description: '无限数量保存，绘本生成7折，4000云币/月',
      mobileDescription: '无限数量保存，绘本生成7折',
      monthlyPrice: 66,
      icon: 'story-membership-premium',
      variant: 'premium'
    }
  ];

  const id = useId();
  const titleId = `story-storage-limit-title-${id}`;
  const descriptionId = `story-storage-limit-description-${id}`;
  const normalizedLevel = computed(() => normalizeStoryMembershipLevel(props.membershipLevel));
  const currentPlanName = computed(() => PLAN_NAMES[normalizedLevel.value]);
  const storyLimit = computed(() => STORY_STORAGE_LIMITS[normalizedLevel.value]);
  const activityMonthlyPrices = ref<Partial<Record<UpgradeLevel, number>>>({});
  const membershipPlans = computed(() =>
    MEMBERSHIP_PLANS.map((plan) => ({
      ...plan,
      monthlyPrice: activityMonthlyPrices.value[plan.level] ?? plan.monthlyPrice
    }))
  );
  const availablePlans = computed(() =>
    membershipPlans.value.filter((plan) => plan.level > normalizedLevel.value)
  );
  let isLoadingActivityPrice = false;
  let hasLoadedActivityPrice = false;

  async function loadActivityPrice() {
    if (isLoadingActivityPrice || hasLoadedActivityPrice) return;

    isLoadingActivityPrice = true;
    try {
      const activity = await fetchAipicturebookPrice();
      const monthlyPrices: Partial<Record<UpgradeLevel, number>> = {};

      for (const item of activity.prices) {
        const annualPrice = activity.active ? item.price : item.original_price;
        if ((item.vip_type === 1 || item.vip_type === 2) && Number.isFinite(annualPrice)) {
          monthlyPrices[item.vip_type] = Math.floor(annualPrice / 12);
        }
      }

      activityMonthlyPrices.value = monthlyPrices;
      hasLoadedActivityPrice = true;
    } catch {
      // Keep the default prices and retry the next time the dialog opens.
    } finally {
      isLoadingActivityPrice = false;
    }
  }

  function closeModal() {
    emit('update:modelValue', false);
  }

  function handleCancel() {
    emit('cancel');
    closeModal();
  }

  function handleManageStories() {
    closeModal();
    emit('manage');
  }

  function handleUpgrade(level: UpgradeLevel) {
    closeModal();
    emit('upgrade', level);
  }

  function handleOverlayClick() {
    if (props.closeOnOverlay) handleCancel();
  }

  function handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape' && props.modelValue) handleCancel();
  }

  watch(
    () => props.modelValue,
    (visible) => {
      if (visible) {
        document.addEventListener('keydown', handleKeydown);
        void loadActivityPrice();
      } else {
        document.removeEventListener('keydown', handleKeydown);
      }
    },
    { immediate: true }
  );

  onBeforeUnmount(() => {
    document.removeEventListener('keydown', handleKeydown);
  });
</script>

<style scoped>
  .story-storage-limit-overlay {
    position: fixed;
    inset: 0;
    z-index: 4000;
    display: flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    padding: 24px;
    background: rgba(0, 0, 0, 0.45);
  }

  .story-storage-limit-dialog {
    width: 664px;
    max-width: calc(100vw - 48px);
    min-height: 390px;
    padding: 26px 20px 40px;
    box-sizing: border-box;
    overflow: hidden;
    border-radius: 12px;
    background: #fff;
    box-shadow: 0 3px 10px rgba(0, 0, 0, 0.05);
    color: #160211;
    font-family: 'Source Han Sans CN VF', 'Noto Sans SC', 'Microsoft YaHei', sans-serif;
  }

  .story-storage-limit-dialog--single-plan {
    min-height: 309px;
  }

  .story-storage-limit-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 24px;
  }

  .story-storage-limit-title {
    margin: 0;
    color: #333940;
    font-family: Inter, 'Noto Sans SC', 'Microsoft YaHei', sans-serif;
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;
  }

  .story-storage-limit-close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    padding: 0;
    border: 0;
    border-radius: 6px;
    background: transparent;
    color: #999;
    cursor: pointer;
  }

  .story-storage-limit-close:hover {
    background: #f5f3fd;
    color: #6033e1;
  }

  .story-storage-limit-close-icon--mobile,
  .membership-plan-description--mobile {
    display: none;
  }

  .story-storage-limit-description {
    margin: 40px 16px 0;
    color: #160211;
    font-size: 16px;
    font-weight: 400;
    line-height: 23px;
  }

  .story-storage-limit-description strong {
    color: #6033e1;
    font-weight: 400;
  }

  .story-storage-limit-description a {
    display: inline;
    padding: 0;
    border: 0;
    background: transparent;
    color: #6033e1;
    font: inherit;
    line-height: inherit;
    text-decoration: underline;
    text-underline-offset: 2px;
    cursor: pointer;
  }

  .story-storage-limit-tip {
    margin: 30px 12px 16px;
    color: #b3aec2;
    font-family: 'Noto Sans SC', 'Microsoft YaHei', sans-serif;
    font-size: 11px;
    font-weight: 500;
    line-height: 17px;
  }

  .story-storage-limit-plans {
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin: 0 11px;
  }

  .membership-plan {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    min-height: 65px;
    padding: 12px 14px;
    box-sizing: border-box;
    border: 1px solid rgba(96, 51, 225, 0.1);
    border-radius: 14px;
    background: #fff;
    box-shadow: 0 8px 24px rgba(96, 51, 225, 0.14);
  }

  .membership-plan-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    flex: 0 0 36px;
    border-radius: 14px;
  }

  .membership-plan--basic .membership-plan-icon {
    background: linear-gradient(135deg, #f0eeff, #e0d8ff);
  }

  .membership-plan--premium .membership-plan-icon {
    background: linear-gradient(135deg, #fff8e6, #ffefc4);
  }

  .membership-plan-copy {
    min-width: 0;
    flex: 1;
    font-family: 'Noto Sans SC', 'Microsoft YaHei', sans-serif;
  }

  .membership-plan-copy h3,
  .membership-plan-copy p {
    margin: 0;
  }

  .membership-plan-copy h3 {
    color: #160211;
    font-size: 13px;
    font-weight: 700;
    line-height: 18px;
  }

  .membership-plan-copy p {
    padding-top: 3px;
    overflow: hidden;
    color: #6e6a7c;
    font-size: 12px;
    font-weight: 400;
    line-height: 18px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .membership-plan-price {
    width: 48px;
    flex: 0 0 48px;
    color: #160211;
    font-family: 'Noto Sans SC', 'Microsoft YaHei', sans-serif;
    font-size: 12px;
    font-weight: 400;
    line-height: 18px;
    white-space: nowrap;
  }

  .membership-plan-price em {
    color: #6033e1;
    font-style: normal;
  }

  .membership-plan--premium .membership-plan-price em {
    color: #ffa114;
  }

  .membership-plan-upgrade {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 64px;
    height: 32px;
    padding: 0;
    flex: 0 0 64px;
    border: 0;
    border-radius: 9px;
    background: #9874ff;
    box-shadow:
      inset 0 1px rgba(255, 255, 255, 0.15),
      0 4px 10px rgba(96, 51, 225, 0.3);
    color: #fff;
    font-family: 'Noto Sans SC', 'Microsoft YaHei', sans-serif;
    font-size: 12px;
    font-weight: 600;
    line-height: 18px;
    cursor: pointer;
  }

  .membership-plan--premium .membership-plan-upgrade {
    background: linear-gradient(90deg, #f4cb76 5%, #ffe2a6 89%);
    color: #d38743;
  }

  .story-storage-limit-close:focus-visible,
  .story-storage-limit-description a:focus-visible,
  .membership-plan-upgrade:focus-visible {
    outline: 2px solid rgba(96, 51, 225, 0.35);
    outline-offset: 2px;
  }

  .story-storage-limit-enter-active,
  .story-storage-limit-leave-active {
    transition: opacity 0.15s ease;
  }

  .story-storage-limit-enter-from,
  .story-storage-limit-leave-to {
    opacity: 0;
  }

  @media (max-width: 767px) {
    .story-storage-limit-overlay {
      align-items: flex-end;
      padding: 0;
      background: rgba(0, 0, 0, 0.3);
    }

    .story-storage-limit-dialog {
      width: 100%;
      max-width: 100vw;
      height: 348px;
      max-height: 100vh;
      min-height: 0;
      padding: 16px;
      overflow-y: auto;
      border-radius: 16px 16px 0 0;
      box-shadow: none;
    }

    .story-storage-limit-dialog--single-plan {
      height: 275px;
    }

    .story-storage-limit-title {
      color: #160211;
      font-family: 'Source Han Sans CN VF', 'Noto Sans SC', 'Microsoft YaHei', sans-serif;
      font-size: 13px;
      font-weight: 700;
      line-height: 16px;
    }

    .story-storage-limit-close {
      width: 44px;
      height: 24px;
      padding: 0 16px;
      box-sizing: border-box;
      border: 1px solid #eff0f7;
      border-radius: 66px;
      background: #fff;
      color: #160211;
    }

    .story-storage-limit-close:hover {
      background: #fff;
      color: #160211;
    }

    .story-storage-limit-close-icon--desktop,
    .membership-plan-description--desktop {
      display: none;
    }

    .story-storage-limit-close-icon--mobile,
    .membership-plan-description--mobile {
      display: inline;
    }

    .story-storage-limit-description {
      width: calc(100% - 3px);
      margin: 16px 0 0;
      font-family: 'Noto Sans SC', 'Microsoft YaHei', sans-serif;
      font-size: 13px;
      line-height: 20.8px;
    }

    .story-storage-limit-tip {
      margin: 16px 0 0;
      font-weight: 400;
    }

    .story-storage-limit-plans {
      gap: 8px;
      margin: 16px 0 0;
    }

    .membership-plan {
      display: flex;
      gap: 10px;
      height: 65px;
      min-height: 65px;
      padding: 12px 14px;
    }

    .membership-plan--basic {
      box-shadow: none;
    }

    .membership-plan-copy p {
      white-space: nowrap;
    }

    .membership-plan-price {
      display: inline;
    }

    .story-storage-limit-enter-active .story-storage-limit-dialog,
    .story-storage-limit-leave-active .story-storage-limit-dialog {
      transition: transform 0.2s ease;
    }

    .story-storage-limit-enter-from .story-storage-limit-dialog,
    .story-storage-limit-leave-to .story-storage-limit-dialog {
      transform: translateY(100%);
    }
  }
</style>
